"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { Check, Star, Sparkles, AlertCircle } from "lucide-react"
import { saveEarlyAccessSignup } from "@/app/actions/early-access"

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  tier: z.enum(["free", "pro", "creator+", "team"], {
    required_error: "Please select a pricing tier",
  }),
  currentPlatforms: z.string().optional(),
  additionalInfo: z.string().optional(),
})

interface EarlyAccessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const tiers = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    description: "Get started with essential features.",
    features: ["2 integrations", "Track Manual Earnings", "View follower + performance stats"],
    popular: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$9.99",
    description: "For creators who want more integrations and insights.",
    features: ["Up to 4 integrations", "Performance Insights", "Custom: Daily, Weekly, Monthly Email Summaries"],
    popular: true,
  },
  {
    id: "creator+",
    name: "Creator+",
    price: "$29.99",
    description: "Unlimited integrations and advanced tools.",
    features: ["Unlimited integrations", "Export Reports", "Custom Media Kit Builder"],
    popular: false,
  },
  {
    id: "team",
    name: "Team",
    price: "$99.99",
    description: "For teams needing collaboration and branded reports.",
    features: ["Team collaboration", "Advanced permissions", "Priority support"],
    popular: false,
  },
]

export function EarlyAccessDialog({ open, onOpenChange }: EarlyAccessDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      tier: "pro",
      currentPlatforms: "",
      additionalInfo: "",
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true)
    setError(null)

    try {
      const result = await saveEarlyAccessSignup(values)

      if (result.success) {
        setIsSuccess(true)
        toast({
          title: "Welcome to early access!",
          description: "We'll notify you as soon as revvu is ready.",
        })
      } else {
        setError(result.message)
        toast({
          title: "Something went wrong",
          description: result.message,
          variant: "destructive",
        })
      }
    } catch (error) {
      const errorMessage = "Please try again later."
      setError(errorMessage)
      toast({
        title: "Something went wrong",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    // Reset form and success state after a delay
    setTimeout(() => {
      setIsSuccess(false)
      setError(null)
      form.reset()
    }, 300)
  }

  if (isSuccess) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="text-center space-y-6 py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-gray-900">You&apos;re in!</h3>
              <p className="text-gray-600">
                Thanks for joining our early access list. We&apos;ll email you as soon as revvu is ready to launch.
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 text-sm font-medium mb-1">
                <Sparkles className="h-4 w-4" />
                Early Access Perks
              </div>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• 30-day free trial (instead of 14 days)</li>
                <li>• 20% off your first year</li>
                <li>• Priority onboarding support</li>
              </ul>
            </div>
            <Button onClick={handleClose} className="w-full">
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Join Early Access</DialogTitle>
          <DialogDescription>
            Be the first to try revvu when it launches. Tell us which plan interests you most and we&apos;ll keep you
            updated.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="your@email.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Pricing Tier Selection */}
            <FormField
              control={form.control}
              name="tier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">Which plan interests you most?</FormLabel>
                  <FormControl>
                    <div>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                      >
                        {tiers.map((tier) => (
                          <div key={tier.id} className="relative">
                            <RadioGroupItem value={tier.id} id={tier.id} className="peer sr-only" />
                            <label
                              htmlFor={tier.id}
                              className={`block cursor-pointer rounded-lg border-2 p-4 transition-all hover:border-green-200 peer-checked:border-green-500 peer-checked:bg-green-50
                                ${field.value === tier.id ? "border-green-500 bg-green-50 shadow-lg scale-105 z-10" : "border-gray-200 bg-white"}
                              `}
                              style={{ boxShadow: field.value === tier.id ? '0 4px 24px 0 rgba(34,197,94,0.15)' : undefined }}
                            >
                              {tier.popular && (
                                <div className="absolute -top-2 left-4">
                                  <div className="bg-green-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                                    <Star className="h-3 w-3" />
                                    Popular
                                  </div>
                                </div>
                              )}
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-gray-900">{tier.name}</h4>
                                <span className="text-lg font-bold text-gray-900">{tier.price}</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-3">{tier.description}</p>
                              <ul className="space-y-1">
                                  {(tier.features ?? []).slice(0, 2).map((feature, index) => (
                                    <li key={index} className="text-xs text-gray-600 flex items-center gap-2">
                                      <Check className="h-3 w-3 text-green-500" />
                                      {feature}
                                    </li>
                                  ))}
                                  {((tier.features ?? []).length > 2) && (
                                    <li className="text-xs text-gray-500">+{(tier.features ?? []).length - 2} more features</li>
                                  )}
                              </ul>
                            </label>
                          </div>
                        ))}
                      </RadioGroup>
                      <div className="text-xs text-gray-500 mt-2">Every plan starts with a 14-day free trial.</div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Current Platforms */}
            <FormField
              control={form.control}
              name="currentPlatforms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What platforms do you currently use? (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., YouTube, Instagram, Amazon Associates, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Additional Info */}
            <FormField
              control={form.control}
              name="additionalInfo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Anything else you&apos;d like us to know? (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Tell us about your biggest revenue tracking challenges..."
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Maybe Later
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 bg-green-600 hover:bg-green-700">
                {isSubmitting ? "Joining..." : "Join Early Access"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
